import { FC, useMemo } from 'react';

import { X } from 'lucide-react';
import { isNullish } from 'remeda';
import { toast } from 'sonner';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';

import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';

import { DailyTaskForm } from '../dailyTaskForm/DailyTaskForm';
import { DailyTaskFormValues } from '../dailyTaskForm/form';
import {
  areRewardsEqual,
  buildDailyTaskRequest,
  getEditFormInitialValues,
  getRewardsPayload,
} from '../dailyTaskForm/utils';

import css from './DailyTaskEditModal.module.css';

import { rewardsService } from 'v2/api/rewardsService';
import { tasksService } from 'v2/api/tasksService';
import { Loader } from 'v2/components/loader/Loader';
import { DailyTaskModel } from 'v2/types/TaskModel';
import { handleHttpError } from 'v2/utils';

interface DailyTaskEditModalProps {
  isOpened: boolean;
  handleClose: VoidFunction;
  onRevalidate: VoidFunction;
  dailyTask: DailyTaskModel | null;
}

export const DailyTaskEditModal: FC<DailyTaskEditModalProps> = ({ isOpened, handleClose, onRevalidate, dailyTask }) => {
  const getRewardIds = (task: DailyTaskModel): string[] =>
    task.isSpecial ? (task.specialRewardIds ?? []) : task.defaultRewardIds;

  const rewardIds = dailyTask ? getRewardIds(dailyTask) : [];

  const { data: initialRewards = [], isLoading: isInitialRewardsLoading } = useSWR(
    isOpened && dailyTask && rewardIds.length > 0 ? ['getRewardsByIds', rewardIds] : null,
    rewardsService.getRewardsByIds,
    { revalidateOnFocus: false, revalidateOnReconnect: false }
  );

  const { trigger: triggerDeleteRewards, isMutating: isDeletingRewards } = useSWRMutation(
    'deleteRewards',
    (_key, { arg }: { arg: string[] }) => rewardsService.deleteRewards(['deleteRewards', arg])
  );

  const { trigger: triggerCreateRewards, isMutating: isCreatingRewards } = useSWRMutation(
    'createRewards',
    rewardsService.createRewards
  );

  const { trigger: triggerUpdateDailyTask, isMutating: isUpdatingDailyTask } = useSWRMutation(
    dailyTask ? ['updateDailyTask', dailyTask.id] : null,
    tasksService.updateDailyTask
  );

  const handleUpdateDailyTask = async (formValues: DailyTaskFormValues) => {
    if (isNullish(dailyTask)) {
      return;
    }

    try {
      const nextRewardsPayload = getRewardsPayload(formValues.rewards, formValues.isSpecial);
      const initialRewardsPayload = initialRewards.map(({ type, value }) => ({ type, value }));
      const isRewardsChanged = !areRewardsEqual(nextRewardsPayload, initialRewardsPayload);

      let resultRewardIds = rewardIds;

      if (isRewardsChanged) {
        if (rewardIds.length > 0) {
          await triggerDeleteRewards(rewardIds);
        }

        resultRewardIds = await triggerCreateRewards(nextRewardsPayload);
      }

      const request = buildDailyTaskRequest({
        values: formValues,
        defaultRewardIds: formValues.isSpecial ? [] : resultRewardIds,
        specialRewardIds: formValues.isSpecial ? resultRewardIds : [],
      });

      await triggerUpdateDailyTask(request);

      toast.success('Daily task has been successfully updated');
      onRevalidate();
      handleClose();
    } catch (error) {
      await handleHttpError(error);
    }
  };

  const initialValues = useMemo(
    () => (dailyTask ? getEditFormInitialValues(dailyTask, initialRewards) : null),
    [dailyTask, initialRewards]
  );

  if (isNullish(dailyTask) || initialValues === null) {
    return null;
  }

  const isProcessing = isDeletingRewards || isCreatingRewards || isUpdatingDailyTask;

  return (
    <Dialog open={isOpened} onClose={handleClose} maxWidth="sm" scroll="paper" fullWidth>
      <DialogTitle className={css.dailyTaskEditModalTitle}>
        Edit Daily Task
        <IconButton onClick={handleClose} size="small">
          <X width={20} height={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ '&&': { pt: 2 } }}>
        {isInitialRewardsLoading ? (
          <Loader />
        ) : (
          <DailyTaskForm
            submitButtonCaption="Edit"
            initialValues={initialValues}
            isEditForm
            isSubmitting={isProcessing}
            onSubmit={handleUpdateDailyTask}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};
